import { Classes, Collapse, CollapseProps } from "@blueprintjs/core";
import React, { useCallback, useMemo } from "react";
import styled, { useTheme } from "styled-components";

import {
  SingleChevronDownIcon,
  SingleChevronDownSmallIcon,
  SingleChevronRightIcon,
  SingleChevronRightSmallIcon,
} from "../components/icons/CustomIcons";
import { useToggleState } from "../hooks/useToggleState";

import { HexClasses } from "./classes.js";

export interface HexCollapseProps extends Pick<CollapseProps, "children"> {
  title: string | React.ReactNode;
  icon?: React.ReactNode;
  rightElement?: React.ReactNode;
  togglePosition?: "left" | "right" | "none";
  duration?: "default" | "medium" | "large" | "none";
  /**
   * Used to override the toggle function and control state externally
   */
  isOpen?: boolean;
  isOpenDefault?: boolean;
  toggleIsOpen?: () => void;
  /**
   * If true, the toggle will be disabled and the toggle icon will be hidden.
   */
  disabled?: boolean;
  keepChildrenMounted?: boolean;
  /**
   * A callback fired when the header is clicked outside of the icon. The
   * default behavior is to toggle the collapse (cancelable by
   * `event.preventDefault()`).
   */
  onHeaderClick?: (event: React.MouseEvent) => void;
  /**
   * A callback fired when the header is keyed down outside of the icon. The
   * default behavior on Space or Enter is to simulate a click on the header
   * (cancelable by `event.preventDefault()`).
   */
  onHeaderKeyDown?: (event: React.KeyboardEvent) => void;
  className?: string;
  "data-test"?: string;

  /**
   * If true, use the small chevron icon
   */
  small?: boolean;
}

const Wrapper = styled.div<{ $duration: number }>`
  .${Classes.COLLAPSE} {
    transition: height ${({ $duration }) => `${$duration}ms`};
    ${({ theme }) => theme.animation.easing};

    .${Classes.COLLAPSE_BODY} {
      transform: none !important;
    }
  }
`;

export const HexCollapse = React.memo(
  React.forwardRef<HTMLDivElement, HexCollapseProps>(function HexCollapse(
    {
      children,
      className,
      "data-test": dataTest,
      disabled = false,
      duration = "default",
      icon,
      isOpen: isOpenOverride,
      isOpenDefault = true,
      keepChildrenMounted = false,
      onHeaderClick: onHeaderClickProp,
      onHeaderKeyDown: onHeaderKeyDownProp,
      rightElement,
      small,
      title,
      toggleIsOpen: toggleOverride,
      togglePosition = "left",
    },
    ref,
  ) {
    const theme = useTheme();
    const [isOpen, _, { toggle: toggleIsOpen }] = useToggleState(isOpenDefault);

    const resolvedIsOpen = isOpenOverride ?? isOpen;
    const resolvedToggle = toggleOverride ?? toggleIsOpen;

    const animationDuration = useMemo(() => {
      if (duration === "medium") {
        return theme.animation.durationMediumMs;
      } else if (duration === "large") {
        return theme.animation.durationLargeMs;
      } else if (duration === "none") {
        return 0;
      }
      return theme.animation.durationMs;
    }, [theme.animation, duration]);

    const toggleHandler = useCallback(
      (evt) => {
        evt.stopPropagation();
        resolvedToggle();
      },
      [resolvedToggle],
    );

    const toggleIcon = useMemo(() => {
      const OpenIcon = small
        ? SingleChevronRightSmallIcon
        : SingleChevronRightIcon;
      const CloseIcon = small
        ? SingleChevronDownSmallIcon
        : SingleChevronDownIcon;

      return disabled ? null : resolvedIsOpen ? (
        <CloseIcon onClick={toggleHandler} />
      ) : (
        <OpenIcon onClick={toggleHandler} />
      );
    }, [disabled, resolvedIsOpen, toggleHandler, small]);

    const handleHeaderClick = useCallback<React.MouseEventHandler>(
      (event) => {
        onHeaderClickProp?.(event);
        if (event.defaultPrevented) return;
        resolvedToggle();
      },
      [onHeaderClickProp, resolvedToggle],
    );

    const handleHeaderKeyDown = useCallback<React.KeyboardEventHandler>(
      (event) => {
        onHeaderKeyDownProp?.(event);
        if (event.defaultPrevented) return;
        if (event.key === "Enter" || event.key === " ") {
          const mouseEvent = new MouseEvent("click", {
            bubbles: true,
            cancelable: true,
          });
          event.target.dispatchEvent(mouseEvent);
        }
      },
      [onHeaderKeyDownProp],
    );

    return (
      <Wrapper ref={ref} $duration={animationDuration} className={className}>
        <ToggleHeader
          aria-expanded={resolvedIsOpen}
          className={HexClasses.HEX_COLLAPSE_TOGGLE_HEADER}
          data-test={dataTest}
          role="button"
          tabIndex={0}
          onClick={handleHeaderClick}
          onKeyDown={handleHeaderKeyDown}
        >
          <ToggleWrapper className={HexClasses.HEX_COLLAPSE_TOGGLE_WRAPPER}>
            {togglePosition === "left" && toggleIcon}
            <HexCollapseHeader className={HexClasses.HEX_COLLAPSE_HEADER}>
              {icon}
              {title}
            </HexCollapseHeader>
          </ToggleWrapper>
          <ToggleWrapper className={HexClasses.HEX_COLLAPSE_TOGGLE_WRAPPER}>
            {rightElement ? <div>{rightElement}</div> : null}
            {togglePosition === "right" && toggleIcon}
          </ToggleWrapper>
        </ToggleHeader>

        {/*
          BP's Collapse doesn't forward props to the underlying element,
          so we can't set any of the necessary attributes for accessibility
          (AccordionPanel: `id`, `role`, `aria-labelledby`)
          (`id` is necessary for AccordionHeader's `aria-controls`)
        */}
        <Collapse
          isOpen={resolvedIsOpen}
          keepChildrenMounted={keepChildrenMounted}
          transitionDuration={animationDuration}
        >
          {children}
        </Collapse>
      </Wrapper>
    );
  }),
);

export const ToggleHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;

  padding: 15px 10px 5px 0;

  svg {
    color: ${({ theme }) => theme.iconColor};
  }

  &:hover {
    cursor: pointer;
  }
`;

export const ToggleWrapper = styled.div`
  display: flex;
  gap: 3px;
  align-items: center;
`;

export const HexCollapseHeader = styled.div`
  display: flex;
  align-items: center;

  color: ${({ theme }) => theme.fontColor.DEFAULT};
  font-weight: ${({ theme }) => theme.fontWeight.MEDIUM};
  font-size: ${({ theme }) => theme.fontSize.DEFAULT};
  user-select: none;

  .${Classes.ICON} {
    margin-right: 6px;
  }
`;
